/**
 * @license
 * @copyright Copyright Motili Inc., 2025 All Rights Reserved
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getClientOrderSettings } from 'common/services/OrderService';
import {
    DOCUMENT_TRADE_TYPE,
    DOCUMENT_TRADE_SUB_TYPE,
    WORK_ORDER_PRIORITY_TYPE,
} from 'common/stores/redux/MetaStore/MetaStoreApi.types';
import { initialOrder } from 'common/stores/redux/OrderCreation/constants';

const initialState = {
    initialized: false,
    order: initialOrder,
    filesInitialized: false,
    historyInitialized: false,
    finances: null,
    snapshot: null,
};

export const createOrderCreationSlice = sliceName => {
    /**
     * Updates the client for an order, fetching and applying client-specific settings
     */
    const updateClient = createAsyncThunk(
        `${sliceName}/updateClient`,
        async (clientId, { getState }) => {
            const state = getState();
            const currentClientId = state[sliceName]?.order?.clientId;

            if (clientId !== currentClientId) {
                const clientSettings = await getClientOrderSettings(clientId);
                return { clientId, clientSettings };
            }

            return null;
        }
    );

    const slice = createSlice({
        name: sliceName,
        initialState,
        reducers: {
            initialize: (state, action) => {
                const _initialOrder = {
                    ...initialOrder,
                    ...action.payload,
                    hasFulfillmentMethods:
                        !!action.payload.hasFulfillmentMethods,
                };
                return {
                    ...state,
                    initialized: true,
                    order: _initialOrder,
                    filesInitialized: false,
                    historyInitialized: false,
                };
            },

            reset: () => {
                return {
                    initialized: false,
                    order: {},
                    filesInitialized: false,
                    historyInitialized: false,
                    finances: null,
                    snapshot: null,
                };
            },

            setOrder: (state, action) => {
                return {
                    ...state,
                    order: action.payload,
                };
            },

            updateOrder: (state, action) => {
                return {
                    ...state,
                    order: {
                        ...state.order,
                        ...action.payload,
                    },
                };
            },

            resetPages: state => {
                return {
                    ...state,
                    initialized: false,
                    filesInitialized: false,
                    historyInitialized: false,
                };
            },

            initializeRoute: (state, action) => {
                return {
                    ...state,
                    [`${action.payload}Initialized`]: true,
                };
            },

            /**
             * Updates a property or properties on the order
             * If key is an array of strings, value is expected to be a respective array of values
             */
            updateKeyValuePair: (state, action) => {
                const { key, value } = action.payload;

                const update = {};

                if (Array.isArray(key)) {
                    key.forEach((k, idx) => {
                        update[k] = value[idx];
                    });
                } else {
                    update[key] = value;
                }
                return {
                    ...state,
                    order: { ...state.order, ...update },
                };
            },

            addNote: (state, action) => {
                const notes = state?.order?.job?.notes || [];
                return {
                    ...state,
                    order: {
                        ...state.order,
                        job: {
                            ...state.order.job,
                            notes: [...notes, action.payload],
                        },
                    },
                };
            },

            initFinancialInformation: (state, action) => {
                return {
                    ...state,
                    finances: action.payload,
                };
            },

            updatePayment: (state, action) => {
                return {
                    ...state,
                    initialized: true,
                    order: action.payload.order,
                    snapshot: action.payload.snapshot,
                };
            },

            updateBilling: (state, action) => {
                return {
                    ...state,
                    initialized: true,
                    order: action.payload.order,
                    snapshot: action.payload.snapshot,
                };
            },

            updateNote: (state, action) => {
                const notes = [...state.order.job.notes];

                const idx = notes.findIndex(n => n.id === action.payload.id);
                if (idx !== -1) {
                    notes.splice(idx, 1, action.payload);
                }
                return {
                    ...state,
                    order: {
                        ...state.order,
                        job: { ...state.order.job, notes },
                    },
                };
            },

            updateSpecialInstructions: (state, action) => {
                return {
                    ...state,
                    order: {
                        ...state.order,
                        instructions: {
                            special: action.payload,
                        },
                    },
                };
            },

            addContact: (state, action) => {
                return {
                    ...state,
                    order: {
                        ...state.order,
                        contacts: [
                            ...(state.order.contacts ?? []),
                            action.payload,
                        ],
                    },
                };
            },

            removeContact: (state, action) => {
                const contacts = (state.order.contacts || []).filter(
                    contact => contact.uuid !== action.payload
                );

                return {
                    ...state,
                    order: { ...state.order, contacts },
                };
            },

            addComment: (state, action) => {
                const comments = state.order.comments || [];

                return {
                    ...state,
                    order: {
                        ...state.order,
                        comments: [...comments, action.payload],
                    },
                };
            },

            updateComment: (state, action) => {
                const comments = [...state.order.comments];

                const idx = comments.findIndex(
                    comment => comment.id === action.payload.id
                );

                if (idx !== -1) {
                    comments.splice(idx, 1, action.payload);
                }

                return {
                    ...state,
                    order: { ...state.order, comments },
                };
            },

            /**
             * Adds a folder attachment to the order
             */
            addFolderAttachment: (state, action) => {
                return {
                    ...state,
                    order: {
                        ...state.order,
                        folder: {
                            ...state.order.folder,
                            folderAttachments: [
                                ...state.order.folder.folderAttachments,
                                action.payload,
                            ],
                        },
                    },
                };
            },

            /**
             * Removes a folder attachment by id from the order
             */
            removeFolderAttachment: (state, action) => {
                const folderAttachments =
                    state.order.folder.folderAttachments.filter(
                        fa => fa.id !== action.payload
                    );
                return {
                    ...state,
                    order: {
                        ...state.order,
                        folder: { ...state.order.folder, folderAttachments },
                    },
                };
            },

            /**
             * Updates fulfillment logistics on an order
             */
            updateFulfillmentLogistics: (state, action) => {
                return {
                    ...state,
                    order: {
                        ...state.order,
                        fulfillmentLogistics: action.payload,
                    },
                };
            },

            /**
             * Sets finance edit mode and manages line items backup
             */
            setFinanceEdit: (state, action) => {
                return {
                    ...state,
                    order: {
                        ...state.order,
                        financeEdit: action.payload,
                        originalLineItems: action.payload
                            ? state.order.lineItems
                            : undefined,
                        lineItems: action.payload
                            ? state.order.lineItems
                            : state.order.originalLineItems,
                    },
                };
            },

            setDefaultTradeAndPriority: state => {
                return {
                    ...state,
                    order: {
                        ...state.order,
                        tradeTypeId: DOCUMENT_TRADE_TYPE.HVAC,
                        tradeSubTypeId: DOCUMENT_TRADE_SUB_TYPE.RESIDENTIAL,
                        priorityId: WORK_ORDER_PRIORITY_TYPE.ROUTINE,
                    },
                };
            },

            updateTradeType: (state, action) => {
                const tradeTypeId = action.payload;

                const update = {
                    tradeTypeId,
                    tradeSubTypeId: state.order.tradeSubTypeId,
                };

                if (
                    tradeTypeId !== DOCUMENT_TRADE_TYPE.PLUMBING &&
                    state.order.tradeSubTypeId ===
                        DOCUMENT_TRADE_SUB_TYPE.WATERHEATER
                ) {
                    update.tradeSubTypeId = DOCUMENT_TRADE_SUB_TYPE.RESIDENTIAL;
                }

                return { ...state, order: { ...state.order, ...update } };
            },

            updateProperty: (state, action) => {
                return {
                    ...state,
                    order: {
                        ...state.order,
                        propertyDisplay: action.payload,
                        propertyId: action.payload?.value?.id,
                    },
                };
            },

            /**
             * Updates fulfillment details and distribution center
             */
            updateFulfillmentDetails: (state, action) => {
                const { fulfillmentDetails, fulfillmentDistributionCenter } =
                    action.payload;
                const details = {
                    fulfillmentTypeId: fulfillmentDetails?.fulfillmentTypeId,
                    fulfillmentDistributionCenterId:
                        fulfillmentDetails?.fulfillmentDistributionCenterId ||
                        fulfillmentDetails?.distributionCenterId,
                    fulfillmentToAddress:
                        fulfillmentDetails?.fulfillmentToAddress,
                    fulfillmentToCity: fulfillmentDetails?.fulfillmentToCity,
                    fulfillmentToRegion:
                        fulfillmentDetails?.fulfillmentToRegion,
                    fulfillmentToPostalCode:
                        fulfillmentDetails?.fulfillmentToPostalCode,
                    fulfillmentTo: fulfillmentDetails?.fulfillmentTo,
                    residentialDelivery:
                        fulfillmentDetails?.residentialDelivery,
                };
                return {
                    ...state,
                    order: {
                        ...state.order,
                        ...details,
                        vendorDistributionCenter: fulfillmentDistributionCenter,
                    },
                };
            },
        },
        extraReducers: builder => {
            builder.addCase(updateClient.fulfilled, (state, action) => {
                if (action.payload) {
                    const { clientId, clientSettings } = action.payload;
                    const {
                        hasFulfillmentMethods,
                        propertyRequired,
                        enableDisplayAvailableStockOption,
                        enableDisplayAHRIFilter,
                    } = clientSettings;

                    const fulfillmentMethod = hasFulfillmentMethods
                        ? state.order.fulfillmentMethod
                        : null;

                    return {
                        ...state,
                        order: {
                            ...state.order,
                            clientId,
                            hasFulfillmentMethods,
                            fulfillmentMethod,
                            propertyRequired,
                            propertyDisplay: null,
                            propertyId: null,
                            enableDisplayAvailableStockOption,
                            enableDisplayAHRIFilter,
                        },
                    };
                }
                // Return state unchanged if no payload
                return state;
            });
        },
    });

    return {
        reducer: slice.reducer,
        actions: slice.actions,
        thunks: { updateClient },
    };
};
